import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, LogIn } from 'lucide-react';
import toast from 'react-hot-toast';

import { useAuth } from '@context/AuthContext';
import Button from '@components/common/Button';
import Loading from '@components/common/Loading';

import loginbg from '../../assets/images/loginbg.png';
import logo from '../../assets/images/logo.png';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError
  } = useForm();

  const onSubmit = async (data) => {
    try {
      await login(data.username, data.password);
      // toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      if (error.response?.data?.field) {
        setError(error.response.data.field, { message });
      } else {
        toast.error(message);
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center"
      style={{
        backgroundImage: `url(${loginbg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: '3rem 1rem'
      }}>
      <motion.div
        style={{
          maxWidth: '28rem',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem'
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center' }}>
          <motion.div
            style={{
              margin: '0 auto',
              height: '5rem',
              width: '5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          >
            <img
              src={logo}
              alt="Icon"
              style={{ height: '4rem', width: 'auto', objectFit: 'contain' }}
            />
          </motion.div>

          <h2 style={{
            fontSize: '1.875rem',
            fontWeight: '700',
            color: 'var(--gray-900)',
            marginBottom: '0.5rem'
          }}>
                        Welcome Back
          </h2>
          <p style={{
            fontSize: '0.875rem',
            color: 'var(--gray-600)',
            marginBottom: '0.25rem'
          }}>
                        Your tickets, just a sign-in away
          </p>
        </div>

        {/* Login Form */}
        <motion.div
          style={{
            backgroundColor: 'white',
            borderRadius: 'var(--radius-xl)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            padding: '2rem'
          }}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.3 }}
        >
          <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Email Field */}
            <div>
              <label htmlFor="username" style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: 'var(--gray-700)',
                marginBottom: '0.5rem'
              }}>
                                Username
              </label>
              <div style={{ position: 'relative' }}>
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '0.75rem',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none'
                }}>
                  <Mail style={{ height: '1.25rem', width: '1.25rem', color: 'var(--gray-400)' }} />
                </div>
                <input
                  id="username"
                  type="text"
                  autoComplete="username"
                  style={{
                    width: '100%',
                    paddingLeft: '2.5rem',
                    paddingRight: '0.75rem',
                    paddingTop: '0.75rem',
                    paddingBottom: '0.75rem',
                    border: errors.username
                      ? '1px solid var(--error-300)'
                      : '1px solid var(--gray-300)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '0.875rem',
                    transition: 'all var(--transition-fast)',
                    outline: 'none'
                  }}
                  placeholder="Enter your username"
                  onFocus={(e) => {
                    e.target.style.borderColor = errors.username ? 'var(--error-500)' : 'var(--primary-500)';
                    e.target.style.boxShadow = errors.username
                      ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
                      : '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = errors.username ? 'var(--error-300)' : 'var(--gray-300)';
                    e.target.style.boxShadow = 'none';
                  }}
                  {...register('username', {
                    required: 'Username is required',
                    minLength: {
                      value: 3,
                      message: 'Username must be at least 3 characters'
                    }
                  })}
                />
              </div>
              {errors.username && (
                <motion.p
                  style={{
                    marginTop: '0.25rem',
                    fontSize: '0.875rem',
                    color: 'var(--error-600)'
                  }}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {errors.username.message}
                </motion.p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: 'var(--gray-700)',
                marginBottom: '0.5rem'
              }}>
                                Password
              </label>
              <div style={{ position: 'relative' }}>
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '0.75rem',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none'
                }}>
                  <Lock style={{ height: '1.25rem', width: '1.25rem', color: 'var(--gray-400)' }} />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  style={{
                    width: '100%',
                    paddingLeft: '2.5rem',
                    paddingRight: '3rem',
                    paddingTop: '0.75rem',
                    paddingBottom: '0.75rem',
                    border: errors.password
                      ? '1px solid var(--error-300)'
                      : '1px solid var(--gray-300)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '0.875rem',
                    transition: 'all var(--transition-fast)',
                    outline: 'none'
                  }}
                  placeholder="Enter your password"
                  onFocus={(e) => {
                    e.target.style.borderColor = errors.password ? 'var(--error-500)' : 'var(--primary-500)';
                    e.target.style.boxShadow = errors.password
                      ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
                      : '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = errors.password ? 'var(--error-300)' : 'var(--gray-300)';
                    e.target.style.boxShadow = 'none';
                  }}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters'
                    }
                  })}
                />
                <button
                  type="button"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '0.75rem',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    borderRadius: 'var(--radius-sm)',
                    transition: 'color var(--transition-fast)'
                  }}
                  onClick={() => setShowPassword(!showPassword)}
                  onMouseEnter={(e) => e.target.style.color = 'var(--gray-600)'}
                  onMouseLeave={(e) => e.target.style.color = 'var(--gray-400)'}
                >
                  {showPassword ? (
                    <EyeOff style={{ height: '1.25rem', width: '1.25rem', color: 'inherit' }} />
                  ) : (
                    <Eye style={{ height: '1.25rem', width: '1.25rem', color: 'inherit' }} />
                  )}
                </button>
              </div>
              {errors.password && (
                <motion.p
                  style={{
                    marginTop: '0.25rem',
                    fontSize: '0.875rem',
                    color: 'var(--error-600)'
                  }}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {errors.password.message}
                </motion.p>
              )}
            </div>

            {/* Remember Me & Forgot Password */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  style={{
                    height: '1rem',
                    width: '1rem',
                    color: 'var(--primary-600)',
                    borderColor: 'var(--gray-300)',
                    borderRadius: 'var(--radius-sm)',
                    marginRight: '0.5rem'
                  }}
                />
                <label htmlFor="remember-me" style={{
                  fontSize: '0.875rem',
                  color: 'var(--gray-700)'
                }}>
                                    Remember me
                </label>
              </div>

              <div>
                <Link
                  to="/forgot-password"
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--primary-600)',
                    textDecoration: 'none',
                    transition: 'color var(--transition-fast)'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'var(--primary-500)'}
                  onMouseLeave={(e) => e.target.style.color = 'var(--primary-600)'}
                >
                                    Forgot your password?
                </Link>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full"
              style={{
                backgroundColor: 'var(--primary-600)',
                color: 'white',
                padding: '0.75rem 1.5rem',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.875rem',
                fontWeight: '600',
                transition: 'background-color var(--transition-fast)',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
              size="lg"
              disabled={isLoading}
              leftIcon={isLoading ? <Loading size="sm" /> : <LogIn style={{ width: '1.25rem', height: '1.25rem' }} />}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
        </motion.div>

        {/* Footer */}
        <motion.div
          style={{
            textAlign: 'center',
            fontSize: '0.875rem',
            color: 'var(--gray-600)'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
                    © 2025 RoadExpress Freight. All rights reserved.
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Login;